// Wave UX-2.2 #5 (2026-05-23) — 외부 바이어 채팅 (낙찰 후 출품자↔낙찰자 1:1).
// 백엔드:
//   GET   /buyer-app/auction/:id/messages  → { items, unreadCount, nextCursor }
//   POST  /buyer-app/auction/:id/messages  { body }
//   PATCH /buyer-app/auction/:id/messages/read
//
// 5s 폴링 (REST-only, WebSocket 미사용 — Flutter 정합 패턴).
// 권한: auction.winner_external_buyer_id === buyer.id 만 (403 NOT_WINNER → 안내 빈 화면).

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized } from './buyerApi';
import AuctionBuyerLayout from './AuctionBuyerLayout';
import { useT } from '../../i18n/I18nProvider';

var POLL_MS = 5000;

function AuctionBuyerChat() {
  var t = useT();
  var navigate = useNavigate();
  var params = useParams();
  var auctionId = params.id;

  var [messages, setMessages] = useState([]);
  var [error, setError] = useState('');
  var [notWinner, setNotWinner] = useState(false);
  var [input, setInput] = useState('');
  var [sending, setSending] = useState(false);
  var [loaded, setLoaded] = useState(false);
  var [showOrig, setShowOrig] = useState({});  // TW-W2 — 메시지별 원문 토글
  var scrollRef = useRef(null);
  var pollerRef = useRef(null);

  var load = useCallback(function() {
    var hitAuth = false;
    authBuyerFetch(API + '/buyer-app/auction/' + auctionId + '/messages?limit=50', {})
      .then(function(r) {
        if (isUnauthorized(r)) { hitAuth = true; throw new Error('unauthorized'); }
        if (r.status === 403) { setNotWinner(true); setLoaded(true); throw new Error('not-winner'); }
        return r.json();
      })
      .then(function(d) {
        if (d && d.success && d.data && Array.isArray(d.data.items)) {
          // 백엔드는 id DESC 순 — 화면은 시간순 (오름차순) 으로 reverse
          var sorted = d.data.items.slice().reverse();
          setMessages(sorted);
        }
        setLoaded(true);
      })
      .catch(function(e) {
        if (e && e.message === 'unauthorized') { handleUnauthorized(navigate); return; }
        if (e && e.message === 'not-winner') return;
        setLoaded(true);
      });
  }, [auctionId, navigate]);

  // 읽음 처리 (열 때 1회)
  var markRead = useCallback(function() {
    if (notWinner) return;
    authBuyerFetch(API + '/buyer-app/auction/' + auctionId + '/messages/read', { method: 'PATCH' })
      .catch(function(){});
  }, [auctionId, notWinner]);

  useEffect(function() {
    load();
    markRead();
    pollerRef.current = setInterval(load, POLL_MS);
    return function() { if (pollerRef.current) clearInterval(pollerRef.current); };
  }, [load, markRead]);

  // 메시지 스크롤 — 새 메시지 도착 시 하단
  useEffect(function() {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  function send() {
    var body = input.trim();
    if (!body || sending) return;
    setSending(true);
    setError('');
    authBuyerFetch(API + '/buyer-app/auction/' + auctionId + '/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body: body }),
    })
      .then(function(r) {
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json();
      })
      .then(function(d) {
        if (d && d.success) {
          setInput('');
          load();
        } else {
          setError(t('chatErrSend'));
        }
      })
      .catch(function(e) {
        if (!(e && e.message === 'unauthorized')) setError(t('chatErrSend'));
      })
      .finally(function(){ setSending(false); });
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  }

  return (
    <AuctionBuyerLayout activeKey={null}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 24px 0', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 130px)' }}>
        <button type="button" onClick={function(){ navigate('/buyer/bids'); }}
          style={{
            background: 'none', border: 'none', color: '#64748b',
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
            padding: '8px 0', marginBottom: 8, alignSelf: 'flex-start',          }}>
          {t('chatBackToBids')}
        </button>

        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginBottom: 16 }}>{t('chatTitle')}</h2>

        {/* 메시지 영역 */}
        {!loaded ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
            {t('commonLoading')}
          </div>
        ) : notWinner ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 14 }}>
            {t('chatNotWinner')}
          </div>
        ) : (
          <>
            <div ref={scrollRef} style={{
              flex: 1, overflowY: 'auto',
              background: '#f8fafc', borderRadius: 12,
              border: '1px solid #e2e8f0',
              padding: 16, marginBottom: 12,
            }}>
              {messages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13 }}>
                  {t('chatEmpty')}
                </div>
              ) : messages.map(function(m) {
                var mine = m.isMine;
                return (
                  <div key={m.id} style={{
                    display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start',
                    marginBottom: 10,
                  }}>
                    <div style={{
                      maxWidth: '70%',
                      background: m.hidden ? '#f1f5f9' : (mine ? '#3b82f6' : 'white'),
                      color: m.hidden ? '#94a3b8' : (mine ? 'white' : '#0f172a'),
                      border: !mine && !m.hidden ? '1px solid #e2e8f0' : 'none',
                      borderRadius: mine ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                      padding: '10px 14px',
                      fontStyle: m.hidden ? 'italic' : 'normal',
                    }}>
                      <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.7, marginBottom: 3 }}>
                        {mine ? t('chatSenderBuyer') : (m.senderName || t('chatSenderSeller'))}
                      </div>
                      <div style={{ fontSize: 13, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {m.hidden ? t('chatHiddenMessage') : ((m.bodyTranslated && !showOrig[m.id]) ? m.bodyTranslated : m.body)}
                      </div>
                      {!m.hidden && m.bodyTranslated && (
                        <button onClick={function() { setShowOrig(function(p) { var n = Object.assign({}, p); n[m.id] = !n[m.id]; return n; }); }}
                          style={{ marginTop: 4, border: 'none', background: 'none', padding: 0, fontSize: 10, color: mine ? 'rgba(255,255,255,0.8)' : '#3b82f6', cursor: 'pointer', textDecoration: 'underline' }}>
                          🌐 {showOrig[m.id] ? '번역 보기' : '원문 보기'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 입력 */}
            {error && (
              <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 8 }}>{error}</div>
            )}
            <div style={{ display: 'flex', gap: 8, paddingBottom: 20 }}>
              <textarea value={input} onChange={function(e){ setInput(e.target.value); }}
                onKeyDown={onKeyDown}
                placeholder={t('chatInputPlaceholder')}
                rows={2}
                style={{
                  flex: 1, padding: '10px 14px',
                  border: '1.5px solid #e2e8f0', borderRadius: 10,
                  fontSize: 13, outline: 'none', resize: 'none',
                  background: 'white',                }}
              />
              <button type="button" onClick={send} disabled={!input.trim() || sending}
                style={{
                  background: !input.trim() || sending ? '#cbd5e1' : 'linear-gradient(135deg, #1B3A5C 0%, #3b82f6 100%)',
                  color: 'white', border: 'none', borderRadius: 10,
                  padding: '0 18px', fontSize: 13, fontWeight: 800,
                  cursor: !input.trim() || sending ? 'not-allowed' : 'pointer',                  alignSelf: 'stretch',
                }}>
                {sending ? t('chatSending') : t('chatSend')}
              </button>
            </div>
          </>
        )}
      </div>
    </AuctionBuyerLayout>
  );
}

export default AuctionBuyerChat;
